<!-- Overlay -->
<div class="lp-overlay" [class.lp-overlay--visible]="open" (click)="onClose()"></div>

<!-- Drawer Panel -->
<div class="lp-panel" [class.lp-panel--open]="open">

  <!-- Header -->
  <div class="lp-header">
    <div class="lp-header-left">
      <i class="ti ti-map-pin"></i>
      <span class="lp-title">Select Location</span>
    </div>
    <button class="lp-close-btn" (click)="onClose()">
      <i class="ti ti-x"></i>
    </button>
  </div>

  <!-- Body -->
  <div class="lp-body">

    <!-- ── Country ─────────────────────────────────────────────────────── -->
    <div class="lp-section-label">
      <i class="ti ti-globe"></i>
      Country
    </div>

    @if (selectedCountry) {
      <div class="lp-selected-chip">
        @if (resolvingCountry) {
          <span class="lp-spinner lp-spinner--sm"></span>
        } @else {
          <span class="lp-flag">{{ selectedCountry.emoji }}</span>
        }
        <span class="lp-selected-name">{{ selectedCountry.name }}</span>
        @if (selectedCountry.iso2) {
          <span class="lp-iso">{{ selectedCountry.iso2 }}</span>
        }
        <button class="lp-chip-remove" (click)="clearCountry()" title="Change country">
          <i class="ti ti-x"></i>
        </button>
      </div>
    } @else {
      <!-- Search input -->
      <div class="lp-search-wrap">
        @if (loadingCountry) {
          <span class="lp-spinner lp-spinner--sm"></span>
        } @else {
          <i class="ti ti-search lp-search-icon"></i>
        }
        <input
          class="lp-search-input"
          type="text"
          placeholder="Type to search country..."
          [(ngModel)]="countrySearch"
          (input)="onCountryInput()"
          autocomplete="off"
        />
        @if (countrySearch) {
          <button class="lp-search-clear" (click)="countrySearch = ''; countrySuggestions = []">
            <i class="ti ti-x"></i>
          </button>
        }
      </div>

      <!-- Suggestions -->
      @if (countrySuggestions.length > 0) {
        <div class="lp-country-list">
          @for (s of countrySuggestions; track s.placeId) {
            <button class="lp-country-item" (click)="selectCountrySuggestion(s)">
              <span class="lp-country-name">{{ s.name }}</span>
            </button>
          }
        </div>
      } @else if (countrySearch.trim().length >= 2 && !loadingCountry) {
        <div class="lp-empty-state">
          <i class="ti ti-map-off"></i>
          <span>No results for "{{ countrySearch }}"</span>
        </div>
      } @else if (!countrySearch) {
        <div class="lp-hint">
          <i class="ti ti-info-circle"></i>
          Start typing a country name...
        </div>
      }
    }

    <!-- ── State / Province ───────────────────────────────────────────── -->
    @if (selectedCountry) {
      <div class="lp-section-label">
        <i class="ti ti-building-community"></i>
        State / Province
      </div>

      @if (selectedState) {
        <div class="lp-selected-chip">
          <span class="lp-selected-name">{{ selectedState.name }}</span>
          <button class="lp-chip-remove" (click)="clearState()" title="Change state">
            <i class="ti ti-x"></i>
          </button>
        </div>
      } @else {
        <div class="lp-cs">
          <div class="lp-cs-trigger" (click)="openStateDropdown()">
            <span class="lp-cs-placeholder">Select state / province...</span>
            <i class="ti ti-chevron-down"></i>
          </div>

          @if (stateDropdownOpen) {
            <div class="lp-cs-panel">
              <div class="lp-cs-search">
                @if (loadingState) {
                  <span class="lp-spinner lp-spinner--sm"></span>
                } @else {
                  <i class="ti ti-search"></i>
                }
                <input
                  type="text"
                  [(ngModel)]="stateSearch"
                  placeholder="Search state..."
                  (input)="onStateInput()"
                  (click)="$event.stopPropagation()"
                  autocomplete="off"
                />
              </div>
              <div class="lp-cs-list">
                @if (stateSuggestions.length > 0) {
                  @for (s of stateSuggestions; track s.placeId) {
                    <button class="lp-cs-option" (click)="selectStateSuggestion(s)">
                      <span>{{ s.name }}</span>
                    </button>
                  }
                } @else if (stateSearch.trim().length >= 2 && !loadingState) {
                  <div class="lp-cs-empty">No results for "{{ stateSearch }}"</div>
                } @else if (!stateSearch) {
                  <div class="lp-cs-empty lp-cs-hint">Type to search states...</div>
                }
              </div>
              <div class="lp-cs-footer">
                <button class="lp-cs-dismiss" (click)="closeStateDropdown()">
                  <i class="ti ti-x"></i> Dismiss
                </button>
              </div>
            </div>
          }
        </div>
      }

      <!-- ── City ──────────────────────────────────────────────────────── -->
      <div class="lp-section-label">
        <i class="ti ti-building"></i>
        City
      </div>

      @if (selectedCity) {
        <div class="lp-selected-chip">
          <span class="lp-selected-name">{{ selectedCity.name }}</span>
          <button class="lp-chip-remove" (click)="clearCity()" title="Change city">
            <i class="ti ti-x"></i>
          </button>
        </div>
      } @else {
        <div class="lp-cs">
          <div class="lp-cs-trigger" (click)="openCityDropdown()">
            <span class="lp-cs-placeholder">Select city (optional)...</span>
            <i class="ti ti-chevron-down"></i>
          </div>

          @if (cityDropdownOpen) {
            <div class="lp-cs-panel">
              <div class="lp-cs-search">
                @if (loadingCity) {
                  <span class="lp-spinner lp-spinner--sm"></span>
                } @else {
                  <i class="ti ti-search"></i>
                }
                <input
                  type="text"
                  [(ngModel)]="citySearch"
                  placeholder="Search city..."
                  (input)="onCityInput()"
                  (click)="$event.stopPropagation()"
                  autocomplete="off"
                />
              </div>
              <div class="lp-cs-list">
                @if (citySuggestions.length > 0) {
                  @for (s of citySuggestions; track s.placeId) {
                    <button class="lp-cs-option" (click)="selectCitySuggestion(s)">
                      <span>{{ s.name }}</span>
                    </button>
                  }
                } @else if (citySearch.trim().length >= 2 && !loadingCity) {
                  <div class="lp-cs-empty">No results for "{{ citySearch }}"</div>
                } @else if (!citySearch) {
                  <div class="lp-cs-empty lp-cs-hint">Type to search cities...</div>
                }
              </div>
              <div class="lp-cs-footer">
                <button class="lp-cs-dismiss" (click)="closeCityDropdown()">
                  <i class="ti ti-x"></i> Dismiss
                </button>
              </div>
            </div>
          }
        </div>
      }

      <!-- ── Timezone (auto-resolved from Google Time Zone API) ─────────── -->
      <div class="lp-section-label">
        <i class="ti ti-clock"></i>
        Timezone
      </div>

      @if (loadingTimezone) {
        <div class="lp-spinner-row">
          <span class="lp-spinner lp-spinner--sm"></span>
          <span>Detecting timezone...</span>
        </div>
      } @else if (selectedTzDetail) {
        <div class="lp-selected-chip lp-selected-chip--tz">
          <i class="ti ti-clock-hour-4"></i>
          <div class="lp-tz-info">
            <span class="lp-selected-name">{{ selectedTzDetail.zoneName }}</span>
            <span class="lp-tz-meta">{{ selectedTzDetail.tzName }} &bull; {{ selectedTzDetail.gmtOffsetName }}</span>
          </div>
        </div>
      } @else if (!selectedCity) {
        <div class="lp-hint">
          <i class="ti ti-info-circle"></i>
          Select a city to auto-detect timezone
        </div>
      } @else {
        <div class="lp-hint lp-hint--warn">
          <i class="ti ti-alert-triangle"></i>
          Timezone could not be determined
        </div>
      }
    }

  </div>

  <!-- Footer -->
  <div class="lp-footer">
    <button class="lp-cancel-btn" (click)="onClose()" [disabled]="applying">
      Cancel
    </button>
    <button
      class="lp-apply-btn"
      [disabled]="applying || !selectedCountry || resolvingCountry || loadingTimezone"
      (click)="onApply()"
    >
      @if (applying) {
        <span class="lp-spinner lp-spinner--sm lp-spinner--white"></span>
        Applying...
      } @else {
        <i class="ti ti-check"></i>
        Apply
      }
    </button>
  </div>

</div>
